<template>
  <el-dropdown trigger="click" @command="handleTimezoneChange">
    <div class="timezone-selector">
      <!-- 当前时间 -->
      <el-icon class="icon"><Clock /></el-icon>
      <span class="timezone-text">{{ currentTimezoneLabel }}</span>
      <span class="current-time">{{ currentTimeFormatted }}</span>
      <el-icon class="arrow"><ArrowDown /></el-icon>
    </div>
    <template #dropdown>
      <el-dropdown-menu>
        <template v-for="(group, groupKey) in groupedTimezones" :key="groupKey">
          <div class="timezone-group-header">
            <span class="group-emoji">{{
              TIMEZONE_GROUPS[groupKey as keyof typeof TIMEZONE_GROUPS].emoji
            }}</span>
            <span class="group-label">{{
              TIMEZONE_GROUPS[groupKey as keyof typeof TIMEZONE_GROUPS].label
            }}</span>
          </div>
          <el-dropdown-item
            v-for="item in group"
            :key="item.value"
            :command="item.value"
            :class="{ 'is-active': item.value === currentTimezone }"
          >
            <div class="timezone-option">
              <div class="timezone-left">
                <span class="timezone-label">{{ item.label }}</span>
                <div class="timezone-info">
                  <span class="timezone-region">{{ item.region }}</span>
                  <span class="separator">·</span>
                  <span class="timezone-offset">{{ item.offset }}</span>
                </div>
              </div>
              <div class="timezone-right">
                <span class="current-time">{{ item.currentTime }}</span>
                <el-icon v-if="item.value === currentTimezone" class="check-icon">
                  <Check />
                </el-icon>
              </div>
            </div>
          </el-dropdown-item>
        </template>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElIcon, ElMessage } from 'element-plus'
import { ArrowDown, Check, Clock } from '@element-plus/icons-vue'
import { useTimezoneStore } from '@/stores/timezone'
import { TIMEZONE_GROUPS, TIMEZONE_OPTIONS, type TimezoneOption } from '@/config/timezone'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// 扩展 dayjs 插件
dayjs.extend(utc)
dayjs.extend(timezone)

const timezoneStore = useTimezoneStore()

const currentTimezone = computed(() => timezoneStore.timezone)
const currentTimezoneLabel = computed(() => {
  const current = TIMEZONE_OPTIONS.find((tz) => tz.value === currentTimezone.value)
  return current?.label || '中国标准时间'
})
const currentTimeFormatted = computed(() => {
  return dayjs(currentTime.value).tz(currentTimezone.value).format('MM-DD HH:mm:ss')
})

// 当前时间
const currentTime = ref(new Date())
let timer: number | undefined

// 计算每个时区的当前时间并分组
const groupedTimezones = computed(() => {
  const withTime = TIMEZONE_OPTIONS.map((tz) => ({
    ...tz,
    currentTime: dayjs(currentTime.value).tz(tz.value).format('MM-DD HH:mm:ss'),
  }))

  // 按组分类
  const grouped: Record<string, (TimezoneOption & { currentTime: string })[]> = {}

  // 按照 asia, europe, america 的顺序分组
  const groupOrder = ['asia', 'europe', 'america'] as const
  groupOrder.forEach((group) => {
    grouped[group] = withTime.filter((tz) => tz.group === group)
  })

  return grouped
})

// 更新时间
const updateTime = () => {
  currentTime.value = new Date()
}

// 挂载时启动定时器
onMounted(() => {
  updateTime()
  timer = window.setInterval(updateTime, 1000)
})

// 卸载时清除定时器
onUnmounted(() => {
  if (timer) {
    clearInterval(timer)
  }
})

const handleTimezoneChange = (timezone: string) => {
  timezoneStore.setTimezone(timezone)
  const selected = TIMEZONE_OPTIONS.find((tz) => tz.value === timezone)
  if (selected) {
    ElMessage.success(`时区已切换至 ${selected.label}`)
  }
}
</script>

<style lang="scss" scoped>
.timezone-selector {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: var(--el-text-color-regular);

  &:hover {
    background-color: var(--el-fill-color-light);
  }

  .icon {
    font-size: 16px;
  }

  .timezone-text {
    font-size: 12px;
    font-weight: 500;
  }

  .arrow {
    font-size: 12px;
    margin-left: 2px;
  }
}

.timezone-group-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--el-text-color-secondary);
  border-bottom: 1px solid var(--el-border-color-lighter);
  background-color: var(--el-fill-color-lighter);

  &:not(:first-child) {
    border-top: 1px solid var(--el-border-color-light);
    margin-top: 4px;
  }

  .group-emoji {
    font-size: 14px;
  }

  .group-label {
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 11px;
  }
}

.el-dropdown-menu__item {
  padding: 6px 12px;

  &.is-active {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
  }
}

.timezone-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 280px;
  padding: 4px 0;

  .timezone-left {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .timezone-label {
      font-size: 13px;
      font-weight: 500;
      line-height: 1.3;
    }

    .timezone-info {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 11px;
      color: var(--el-text-color-secondary);

      .timezone-region {
        font-weight: 500;
        font-family: monospace;
      }

      .separator {
        color: var(--el-text-color-placeholder);
      }

      .timezone-offset {
        font-family: monospace;
        color: var(--el-text-color-regular);
      }
    }
  }

  .timezone-right {
    display: flex;
    align-items: center;
    gap: 8px;

    .current-time {
      font-size: 12px;
      color: var(--el-text-color-regular);
      font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Courier New', monospace;
      font-weight: 500;
      background-color: var(--el-fill-color-light);
      padding: 2px 6px;
      border-radius: 3px;
    }

    .check-icon {
      font-size: 14px;
      color: var(--el-color-primary);
      margin-left: 4px;
    }
  }
}
</style>
